
import { computed, defineComponent, provide, ref } from "vue";
import { StoreKey } from "./symbols";
import Store from "./Store";
import SubtitleParser from "./utils/SubtitleParser";
import SrtSubtitles from "./utils/SrtSubtitles";

import SubtitleViewer from "./components/SubtitleViewer.vue";
import SubtitleTracklistViewer from "./components/SubtitleTracklistViewer.vue";
import VideoPlayer from "./components/VideoPlayer.vue";

const isSubtitleFile = function(file: File) {
  return /\.(srt|ass)$/.test(file.name);
};

export default defineComponent({
  name: "App",

  components: {
    SubtitleViewer,
    SubtitleTracklistViewer,
    VideoPlayer
  },

  setup() {
    provide(StoreKey, Store);
    const offsetInputElement = ref<HTMLInputElement>();

    const subtitleFileName = ref("");
    const subtitlesUrl = ref("");
    const videoUrl = ref("");
    const loadVideoError = ref("");
    const loadSubtitlesError = ref("");

    const loadSubtitleFile = (file: File) => {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = () => {
        const vtt = SubtitleParser.convertFileToVtt(reader.result as string);
        if (!vtt) {
          subtitleFileName.value = "";
          loadSubtitlesError.value = `Failed to parse subtitles (.ass or .srt): ${file.name}`;
          return;
        }
        subtitlesUrl.value = `data:text/vtt;charset=utf-8,${encodeURIComponent(
          vtt
        )}`;
        subtitleFileName.value = file.name;
        loadSubtitlesError.value = "";

        if (!offsetInputElement.value) {
          return;
        }
        offsetInputElement.value.value = offsetInputElement.value.defaultValue;
      };
    };

    const loadVideoFile = (file: File) => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl.value);
      }
      videoUrl.value = URL.createObjectURL(file);
      loadVideoError.value = "";
    };

    const downloadSubtitlesLink = computed(() => {
      const text = SrtSubtitles.convertCaptionsToSrt(Store.state.cues);
      if (!text) {
        return;
      }
      const fileType = "srt";
      const blob = new Blob([text], { type: fileType });
      return URL.createObjectURL(blob);
    });

    return {
      Store,
      offsetInputElement,
      subtitleFileName,
      subtitlesUrl,
      videoUrl,
      loadVideoError,
      loadSubtitlesError,
      downloadSubtitlesLink,
      onDropEvent: (e: DragEvent) => {
        if (!e.dataTransfer) {
          return;
        }
        [...e.dataTransfer.files].forEach(file => {
          isSubtitleFile(file) ? loadSubtitleFile(file) : loadVideoFile(file);
        });
      },
      onVideoError: (message: string) => {
        loadVideoError.value = message;
      }
    };
  }
});
